import { notification } from 'antd';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { formatarMoeda } from '../../../../helpers/formatador';
import { getEstadosPermitidos } from '../../../../helpers/user';
import apiDatalake from '../../../../services/apiDatalake';
import { Container, Spin, Styles } from './styles';

const HistoricoDeFreteNoLote = ({
  dataInicial,
  dataFinal,
  loteId,
  isMarketplace,
  width,
  height,
}) => {
  const [dataDash, setDataDash] = useState([]);
  const [loading, setLoading] = useState(false);

  const transformaData = variacao => {
    const label = [];
    variacao.forEach(arr => {
      const dados = arr.data.reduce((obj, val) => {
        obj.frete_empresa = parseInt(val.frete, 10);
        obj.frete_motorista = parseInt(val.frete_motorista, 10);
        return obj;
      }, {});
      label.push({
        name: arr.label,
        ...dados,
      });
    });
    return label;
  };

  const createLabelDaily = (dtInicial, dtFinal) => {
    const labels = [];
    const inicio = moment(dtInicial).startOf('days');
    const fim = moment(dtFinal).endOf('days');
    for (
      let dtIni = inicio;
      dtIni.isBefore(fim, 'minute');
      dtIni.add(1, 'days')
    ) {
      labels.push(dtIni.format('D/M'));
    }

    return labels;
  };

  const formatterTooltip = (value, name) => [
    formatarMoeda(value),
    name.replace('_', ' '),
  ];

  const formatterLegendText = (value, entry) => {
    const { color } = entry;
    return (
      <span style={{ color }}>{`média do  ${value.replace('_', ' ')}`}</span>
    );
  };

  const getVariacao = useCallback(async () => {
    try {
      setLoading(true);

      const body = {
        tipo: 'diario',
        dataInicial: dataInicial.format(),
        dataFinal: dataFinal.format(),
        lotes: [loteId],
        estados: getEstadosPermitidos(),
      };

      const { data: variacoesDeFrete } = await apiDatalake.post(
        '/api-variacao-frete',
        body
      );

      const labels = createLabelDaily(dataInicial, dataFinal);

      const dados = labels.map(label => ({
        data: [],
        label,
      }));

      dados.forEach(dado => {
        dado.data = [
          ...dado.data,
          ...variacoesDeFrete.filter(variacao => variacao.label === dado.label),
        ];
      });

      const hasDados = dados.filter(dado => dado.data.length > 0);

      if (!hasDados.length) {
        notification.warning({
          message: 'Não foram encontrados dados de frete para esse lote.',
        });
      }

      const dataDashResponse = transformaData(dados);
      setDataDash(dataDashResponse);
    } catch (err) {
      notification.error({
        message: 'Ops, não conseguimos fazer a busca.',
        description:
          'Caso o problema persista, entre em contato com nosso suporte.',
      });
    } finally {
      setLoading(false);
    }
  }, [dataInicial, dataFinal, loteId]);

  useEffect(() => {
    getVariacao();
  }, [getVariacao]);

  return (
    <>
      <Styles />
      <Container
        type="flex"
        align="middle"
        justify="center"
        className={loading ? 'chart-custom-loading' : 'chart-custom'}
      >
        <Spin size="large" spinning={loading} />
        <ResponsiveContainer width={width} height={height}>
          <ComposedChart
            data={dataDash}
            margin={{
              top: 18,
              right: 0,
              left: 0,
              bottom: 10,
            }}
            barGap={12}
            barCategoryGap={120}
          >
            <CartesianGrid horizontal vertical={false} />
            <XAxis dataKey="name" />

            <YAxis
              yAxisId="right"
              type="number"
              orientation="right"
              tickFormatter={value => formatarMoeda(value, 0, 1)}
              style={{ fontSize: '12px' }}
            />

            <Tooltip formatter={formatterTooltip} />
            <Legend formatter={formatterLegendText} />

            <Line
              yAxisId="right"
              key={isMarketplace ? 'frete_motorista' : 'frete_empresa'}
              dataKey={isMarketplace ? 'frete_motorista' : 'frete_empresa'}
              stroke="#E47515"
              type="linear"
              strokeWidth={2}
              activeDot={{ r: 4 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Container>
    </>
  );
};

HistoricoDeFreteNoLote.propTypes = {
  dataInicial: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.any,
  ]).isRequired,
  dataFinal: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.any,
  ]).isRequired,
  loteId: PropTypes.number.isRequired,
  isMarketplace: PropTypes.bool.isRequired,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

HistoricoDeFreteNoLote.defaultProps = {
  width: '100%',
  height: '100%',
};

export default HistoricoDeFreteNoLote;
